import React, { useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { APP_NAME, APP_TAGLINE } from '../../constants';
import homeHeroIcon from '../../assets/homeHeroIcon.png';

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isReady } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  if (isReady && isAuthenticated) return <Navigate to="/dashboard" replace />;

  const validate = () => {
    const next: typeof errors = {};
    if (!email.trim()) next.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) next.email = 'Enter a valid email';
    if (!password) next.password = 'Password is required';
    else if (password.length < 8) next.password = 'Password must be at least 8 characters';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!validate()) return;
    setLoading(true);
    try {
      await login(email.trim(), password, remember);
      navigate('/dashboard');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background:
          'radial-gradient(ellipse at top left, rgba(16,185,129,0.10), transparent 45%), var(--color-bg-app)',
      }}
    >
      <Paper
        variant="outlined"
        sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 3 }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            component="img"
            src={homeHeroIcon}
            alt={APP_NAME}
            sx={{
              height: 48,
              width: 'auto',
              maxWidth: 180,
              objectFit: 'contain',
              display: 'block',
              mb: 2,
              borderRadius: 1,
            }}
          />
          <Typography variant="h1" sx={{ mb: 0.5 }}>
            Sign in to {APP_NAME}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {APP_TAGLINE}
          </Typography>
        </Box>

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={Boolean(errors.password)}
            helperText={errors.password}
            margin="normal"
            required
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
              label="Remember Me"
            />
            <Link component={RouterLink} to="/forgot-password" underline="hover" variant="body2">
              Forgot Password?
            </Link>
          </Box>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            disabled={loading}
            sx={{ mt: 2.5 }}
          >
            {loading ? 'Signing in…' : 'Login'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;
